import { useMemo, useState, useContext, useEffect } from "react";
import { Stack, IconButton, PrimaryButton, TextField, Text, Dropdown, IDropdownOption } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse, FeedbackRating, SpeechConfig, getCitationFilePath, submitFeedbackApi } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";
import { useAuthClient } from "../../useAuthClient";
import { getToken, requireLogin } from "../../authConfig";
import { LoginContext } from "../../loginContext";

interface Props {
    answer: ChatAppResponse;
    index: number;
    question: string;
    sessionId?: string | Record<string, any> | null;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
    showThoughtProcess?: boolean;
    showFeedbackControls?: boolean;
}

export const Answer = ({
    answer,
    index,
    question,
    sessionId,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser,
    showThoughtProcess = true,
    showFeedbackControls = true
}: Props) => {
    const followupQuestions = answer.context?.followup_questions;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming, onCitationClicked), [answer]);
    const { t } = useTranslation();
    const negativeFeedbackOptions: IDropdownOption<string>[] = useMemo(
        () => [
            { key: "incorrectInfo", text: t("feedback.reasonOptions.incorrectInfo") },
            { key: "missingInfo", text: t("feedback.reasonOptions.missingInfo") },
            { key: "misinterpretedRequest", text: t("feedback.reasonOptions.misinterpretedRequest") },
            { key: "functionalEnhancement", text: t("feedback.reasonOptions.functionalEnhancement") },
            { key: "other", text: t("feedback.reasonOptions.other") }
        ],
        [t]
    );
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    const [copied, setCopied] = useState(false);
    const auth = useAuthClient();
    const { loggedIn } = useContext(LoginContext);
    const [selectedRating, setSelectedRating] = useState<FeedbackRating | null>(null);
    const [feedbackReason, setFeedbackReason] = useState<string | undefined>();
    const [feedbackComment, setFeedbackComment] = useState("");
    const [showFeedbackInput, setShowFeedbackInput] = useState(false);
    const [feedbackError, setFeedbackError] = useState<string>();
    const [feedbackSuccess, setFeedbackSuccess] = useState(false);
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [hasSubmittedRating, setHasSubmittedRating] = useState(false);
    const [hasSubmittedComment, setHasSubmittedComment] = useState(false);
    const answerText = answer.message?.content ?? "";

    useEffect(() => {
        setSelectedRating(null);
        setFeedbackReason(undefined);
        setFeedbackComment("");
        setShowFeedbackInput(false);
        setFeedbackError(undefined);
        setFeedbackSuccess(false);
        setHasSubmittedRating(false);
        setHasSubmittedComment(false);
    }, [answerText, sessionId, index]);

    useEffect(() => {
        if (!feedbackSuccess) {
            return;
        }
        const timeout = setTimeout(() => setFeedbackSuccess(false), 3000);
        return () => clearTimeout(timeout);
    }, [feedbackSuccess]);

    const handleCopy = () => {
        // Single replace to remove all HTML tags to remove the citations
        const textToCopy = sanitizedAnswerHtml.replace(/<a [^>]*><sup>\d+<\/sup><\/a>|<[^>]+>/g, "");

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    const disableFeedbackForLogin = requireLogin && !loggedIn;
    const normalizedSessionId =
        typeof sessionId === "string"
            ? sessionId
            : sessionId && typeof sessionId === "object"
              ? (() => {
                    const candidate = sessionId.conversation_id ?? sessionId.id;
                    return candidate ? String(candidate) : undefined;
                })()
              : undefined;
    const hasSessionId = Boolean(normalizedSessionId);
    const feedbackButtonsDisabled = !hasSessionId || isStreaming || disableFeedbackForLogin || isSubmittingFeedback || hasSubmittedRating;
    const commentSubmitDisabled =
        (!selectedRating || !hasSessionId || isSubmittingFeedback || hasSubmittedComment) ||
        (selectedRating === "down" ? !feedbackReason : !feedbackComment.trim());

    const submitFeedback = async (rating: FeedbackRating, commentText?: string) => {
        if (!normalizedSessionId) {
            setFeedbackError(t("feedback.sessionUnavailable"));
            return;
        }
        setIsSubmittingFeedback(true);
        setFeedbackError(undefined);
        try {
            const token = await getToken(auth);
            await submitFeedbackApi(
                {
                    sessionId: normalizedSessionId,
                    responseIndex: index,
                    rating,
                    question,
                    answer: answerText,
                    comment: commentText ?? "",
                },
                token
            );
            if (commentText && commentText.trim().length > 0) {
                setHasSubmittedComment(true);
            } else {
                setHasSubmittedRating(true);
            }
            setFeedbackSuccess(true);
        } catch (err) {
            console.error("Failed to submit feedback", err);
            setFeedbackError(t("feedback.submitError"));
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const handleFeedbackSelection = (rating: FeedbackRating) => {
        if (!sessionId || isStreaming || disableFeedbackForLogin || isSubmittingFeedback || hasSubmittedRating) {
            return;
        }
        setFeedbackReason(undefined);
        setSelectedRating(rating);
        setShowFeedbackInput(true);
        setHasSubmittedRating(true);
        void submitFeedback(rating);
    };

    const handleFeedbackSubmit = () => {
        if (!selectedRating || !normalizedSessionId || isSubmittingFeedback) {
            return;
        }
        const trimmedComment = feedbackComment.trim();
        const selectedReason =
            selectedRating === "down"
                ? negativeFeedbackOptions.find(option => option.key === feedbackReason)?.text ?? feedbackReason ?? ""
                : "";
        if (selectedRating === "down" && !selectedReason) {
            return;
        }
        const formattedComment =
            selectedRating === "down"
                ? `Reason: ${selectedReason}${trimmedComment ? ` | Comments: ${trimmedComment}` : ""}`
                : trimmedComment;
        if (!formattedComment) {
            return;
        }
        void submitFeedback(selectedRating, formattedComment);
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            ariaLabel={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        />
                        {showThoughtProcess && (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "Lightbulb" }}
                                title={t("tooltips.showThoughtProcess")}
                                ariaLabel={t("tooltips.showThoughtProcess")}
                                onClick={() => onThoughtProcessClicked()}
                                disabled={!answer.context.thoughts?.length || isStreaming}
                            />
                        )}
                        {showSpeechOutputAzure && (
                            <SpeechOutputAzure answer={sanitizedAnswerHtml} index={index} speechConfig={speechConfig} isStreaming={isStreaming} />
                        )}
                        {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText}>
                    <ReactMarkdown children={sanitizedAnswerHtml} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} />
                </div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>{t("citationWithColon")}</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const path = getCitationFilePath(x);
                            return (
                                <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(path)}>
                                    {`${++i}. ${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {showFeedbackControls && (
                <Stack.Item>
                    <div className={styles.feedbackContainer}>
                        <Text className={styles.feedbackPrompt}>{t("feedback.prompt")}</Text>
                        <div className={styles.feedbackButtons}>
                            <IconButton
                                className={`${styles.feedbackButton} ${selectedRating === "up" ? styles.feedbackButtonSelected : ""}`}
                                iconProps={{ iconName: selectedRating === "up" ? "LikeSolid" : "Like" }}
                                ariaLabel={t("feedback.thumbsUp")}
                                title={t("feedback.thumbsUp")}
                                disabled={feedbackButtonsDisabled}
                                onClick={() => handleFeedbackSelection("up")}
                            />
                            <IconButton
                                className={`${styles.feedbackButton} ${selectedRating === "down" ? styles.feedbackButtonSelected : ""}`}
                                iconProps={{ iconName: selectedRating === "down" ? "DislikeSolid" : "Dislike" }}
                                ariaLabel={t("feedback.thumbsDown")}
                                title={t("feedback.thumbsDown")}
                                disabled={feedbackButtonsDisabled}
                                onClick={() => handleFeedbackSelection("down")}
                            />
                        </div>
                    </div>
                    {disableFeedbackForLogin && <div className={styles.feedbackHelper}>{t("feedback.loginRequired")}</div>}
                    {!hasSessionId && !disableFeedbackForLogin && <div className={styles.feedbackHelper}>{t("feedback.sessionUnavailable")}</div>}
                    {showFeedbackInput && (
                        <div className={styles.feedbackCommentContainer}>
                            {selectedRating === "down" && (
                                <Dropdown
                                    label={t("feedback.reasonLabel")}
                                    placeholder={t("feedback.reasonPlaceholder")}
                                    options={negativeFeedbackOptions}
                                    selectedKey={feedbackReason}
                                    onChange={(_, option) => setFeedbackReason((option?.key as string | undefined) ?? undefined)}
                                    disabled={hasSubmittedComment}
                                />
                            )}
                            <TextField
                                multiline
                                resizable={false}
                                borderless={false}
                                placeholder={t("feedback.commentPlaceholder")}
                                value={feedbackComment}
                                onChange={(_, val) => setFeedbackComment(val ?? "")}
                                disabled={hasSubmittedComment}
                                maxLength={500}
                            />
                            <PrimaryButton text={t("feedback.submit")} onClick={handleFeedbackSubmit} disabled={commentSubmitDisabled} />
                        </div>
                    )}
                    {(feedbackError || feedbackSuccess) && (
                        <div className={`${styles.feedbackStatus} ${feedbackError ? styles.feedbackError : styles.feedbackSuccess}`}>
                            {feedbackError ?? t("feedback.success")}
                        </div>
                    )}
                </Stack.Item>
            )}
        </Stack>
    );
};
